import { Component, EventEmitter, forwardRef, Input, OnInit, Optional, Output, SkipSelf } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { IPhoneModel } from 'src/app/core/models/RegisterStrangerRequest';
import { CountriesResponse } from 'src/app/core/models/country';
import { LangEnum } from 'src/app/core/shared/enums/langEnum';
import { RegistrationService } from 'src/app/modules/auth/services/registration.service';

@Component({
  selector: 'app-phone-custom',
  templateUrl: './phone-custom.component.html',
  styleUrls: ['./phone-custom.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneCustomComponent),
      multi: true,
    },
  ],
})
export class PhoneCustomComponent implements ControlValueAccessor, OnInit {
  phoneCountryCode: string = ''; //код страны в формате Iso
  phoneNumber: string = ''; //номер телефона в формате 79991234567
  phoneWithoutCode: string = '';
  selectedCountry: CountriesResponse = {} as CountriesResponse;
  isInvalid: boolean = false;
  @Input() pathIcons = 'assets/shared/img/flag/';
  @Input() maxLength: number = 20;
  @Input() minLength: number = 0;
  @Input() placeholder: string = 'Телефон';
  @Input() placeholderSearch: string = 'Поиск';
  @Input() onlyCountriesCodes: Array<string> = []; //коды стран, что должны выводиться в списке
  @Input() required = false;
  @Input() disabled = false;
  @Input() errorRequired = 'Телефон обязательное поле';
  @Input() allCountries: Array<CountriesResponse> = [];

  @Output() readonly countryChange = new EventEmitter<CountriesResponse>();
  @Output() readonly phoneChange = new EventEmitter<IPhoneModel>();

  constructor(
    @Optional() @SkipSelf() public ngControl: NgControl,
    private registrationService: RegistrationService,
    private translocoService: TranslocoService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.allCountries = this.allCountries.sort((one, two) => (one.orderNumber < two.orderNumber ? -1 : 1));
    this.init();
  }

  onChange: (value: IPhoneModel) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: IPhoneModel): void {
    if (value && (value?.phoneCountryCode || value?.phoneNumber)) {
      let isoChanged = this.phoneCountryCode != (value.phoneCountryCode ?? '643');
      this.phoneCountryCode = value.phoneCountryCode ?? '643';
      this.phoneNumber = value.phoneNumber ?? '';
      this.phoneWithoutCode = value.phoneNumber ?? '';
      // выбор страны по ISO, если не нашли, то первую
      if (isoChanged && this.allCountries.length)
        this.setSelectedCountry(this.allCountries.find((x) => x.code == this.phoneCountryCode) ?? this.allCountries[0]);
      this.updatePhoneWithoutCode();
    }
  }
  registerOnChange(fn: (value: IPhoneModel) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  updateValue() {
    let phone: IPhoneModel = { phoneCountryCode: this.selectedCountry.code, phoneNumber: this.selectedCountry.phoneCode + this.phoneWithoutCode };
    this.phoneChange.emit(phone);
    this.onChange(phone);
  }
  langIsRu() {
    return this.translocoService.getActiveLang() === LangEnum.RU;
  }
  getCountriesList(filterText: string): CountriesResponse[] {
    const isRu = this.langIsRu();
    return this.allCountries.filter(
      (x) => x.name.toLocaleLowerCase().includes(filterText.toLowerCase()) || x.inEnglish.toLocaleLowerCase().includes(filterText.toLowerCase())
    );
  }
  onBlur() {
    this.isInvalid = (this.required && !this.phoneWithoutCode) || (this.phoneNumber.length < this.minLength && this.phoneWithoutCode.length > 0);
  }
  public onInputKeyPress(event: KeyboardEvent): void {
    const allowedChars = /[0-9]/;
    const allowedCtrlChars = /[axcv]/; // Allows copy-pasting
    const allowedOtherKeys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End', 'Insert', 'Delete', 'Backspace'];

    if (!allowedChars.test(event.key) && !(event.ctrlKey && allowedCtrlChars.test(event.key)) && !allowedOtherKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  countryName(country: CountriesResponse) {
    return this.langIsRu() ? country.name : country.inEnglish;
  }

  init() {
    if (this.onlyCountriesCodes.length) {
      this.allCountries = this.allCountries.filter((c) => this.onlyCountriesCodes.includes(c.code));
    }

    if (!this.phoneCountryCode) {
      this.phoneCountryCode = '643';
    }
    this.setSelectedCountry(this.allCountries.find((x) => x.code == this.phoneCountryCode) ?? this.allCountries[0]);
    this.updatePhoneWithoutCode();
  }
  //обрежем код страны
  updatePhoneWithoutCode() {
    if (this.phoneNumber) {
      const index = this.phoneNumber.indexOf(this.selectedCountry.phoneCode);
      if (index !== -1) {
        this.phoneWithoutCode = this.phoneNumber.slice(index + this.selectedCountry.phoneCode.length);
      }
    }
  }
  setSelectedCountry(country: CountriesResponse) {
    this.selectedCountry = country;
    this.countryChange.emit(country);
  }

  public onPhoneNumberChange(): void {
    this.updateValue();
  }

  get errors() {
    return this.ngControl?.errors;
  }
}
